import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { 
  Video, Plus, Calendar, Clock, Users, BookOpen, 
  ExternalLink, Play, Square, RefreshCw, Edit, Trash2,
  Eye, Link2, MonitorPlay, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';

const PLATFORMS = [
  { value: 'zoom', label: 'Zoom' },
  { value: 'google_meet', label: 'Google Meet' },
  { value: 'teams', label: 'Microsoft Teams' },
  { value: 'webex', label: 'Cisco Webex' },
  { value: 'other', label: 'Autre (lien personnalisé)' }
];

const LiveSessionsAdminPage = ({ onMenuClick }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [groups, setGroups] = useState([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReplayModal, setShowReplayModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course_id: '',
    trainer_id: '',
    group_id: '',
    scheduled_date: '',
    start_time: '',
    duration_minutes: 60,
    meeting_platform: 'zoom',
    live_url: '',
    meeting_id: '',
    meeting_password: ''
  });
  
  const [replayUrl, setReplayUrl] = useState('');

  const fetchSessions = useCallback(async () => {
    try {
      let url = '/live-sessions';
      if (statusFilter) url += `?status=${statusFilter}`;
      const response = await api.get(url);
      setSessions(response.data);
    } catch (error) {
      console.error('Erreur chargement sessions:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchData = useCallback(async () => {
    // Load independently so a courses/trainers failure never empties the Groupe list.
    const [coursesRes, trainersRes, groupsRes] = await Promise.all([
      api.get('/content/courses')
        .catch(() => api.get('/courses'))
        .catch(() => ({ data: [] })),
      api.get('/trainers').catch(() => ({ data: [] })),
      api.get('/groups')
        .catch(() => api.get('/documents/groups'))
        .catch((err) => {
          console.error('Erreur chargement groupes:', err);
          return { data: [] };
        }),
    ]);

    const coursesData = Array.isArray(coursesRes.data)
      ? coursesRes.data
      : Array.isArray(coursesRes.data?.items)
        ? coursesRes.data.items
        : [];
    const trainersData = Array.isArray(trainersRes.data)
      ? trainersRes.data
      : Array.isArray(trainersRes.data?.trainers)
        ? trainersRes.data.trainers
        : [];
    const groupsData = Array.isArray(groupsRes.data)
      ? groupsRes.data
      : Array.isArray(groupsRes.data?.items)
        ? groupsRes.data.items
        : [];

    setCourses(coursesData);
    setTrainers(trainersData);
    setGroups(groupsData);
  }, []);

  useEffect(() => {
    fetchSessions();
    fetchData();
  }, [fetchSessions, fetchData]);

  // Auto-open create modal if ?action=create in URL
  useEffect(() => {
    if (searchParams.get('action') === 'create') {
      openCreateModal();
      // Replace (not push) so the Back button returns to the dashboard, not this modal.
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      course_id: '',
      trainer_id: '',
      group_id: '',
      scheduled_date: '',
      start_time: '',
      duration_minutes: 60,
      meeting_platform: 'zoom',
      live_url: '',
      meeting_id: '',
      meeting_password: ''
    });
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
    // Refresh groups/courses/trainers when opening (e.g. after creating a group)
    fetchData();
  };

  const openEditModal = (session) => {
    setSelectedSession(session);
    setFormData({
      title: session.title || '',
      description: session.description || '',
      course_id: session.course_id || '',
      trainer_id: session.trainer_id || '',
      group_id: session.group_id || '',
      scheduled_date: session.scheduled_date || '',
      start_time: session.start_time || '',
      duration_minutes: session.duration_minutes || 60,
      meeting_platform: session.meeting_platform || 'zoom',
      live_url: session.live_url || '',
      meeting_id: session.meeting_id || '',
      meeting_password: session.meeting_password || ''
    });
    setShowEditModal(true);
    fetchData();
  };

  const openDetailModal = (session) => {
    setSelectedSession(session);
    setShowDetailModal(true);
  };

  const openReplayModal = (session) => {
    setSelectedSession(session);
    setReplayUrl(session.replay_url || '');
    setShowReplayModal(true);
  };

  const buildSessionPayload = () => {
    const scheduled_at = new Date(
      `${formData.scheduled_date}T${formData.start_time}:00`
    );
    if (Number.isNaN(scheduled_at.getTime())) {
      throw new Error('Date ou heure invalide');
    }

    const toId = (value) => {
      if (value === '' || value === null || value === undefined) return null;
      const n = parseInt(value, 10);
      return Number.isNaN(n) ? null : n;
    };

    return {
      title: formData.title.trim(),
      description: formData.description?.trim() || null,
      course_id: toId(formData.course_id),
      trainer_id: toId(formData.trainer_id),
      group_id: toId(formData.group_id),
      scheduled_at: scheduled_at.toISOString(),
      duration_minutes: formData.duration_minutes || 60,
      meeting_platform: formData.meeting_platform || 'zoom',
      live_url: formData.live_url?.trim() || null,
      meeting_id: formData.meeting_id?.trim() || null,
      meeting_password: formData.meeting_password?.trim() || null,
    };
  };

  const getApiErrorMessage = (error, fallback) => {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((e) => e.msg || e).join(', ');
    }
    return error.message || fallback;
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.scheduled_date || !formData.start_time) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    try {
      await api.post('/live-sessions', buildSessionPayload());
      
      toast.success('Session créée avec succès');
      setShowCreateModal(false);
      resetForm();
      fetchSessions();
    } catch (error) {
      console.error('Error creating live session:', error);
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création'));
    }
  };

  const handleUpdate = async () => {
    try {
      const payload = buildSessionPayload();
      await api.put(`/live-sessions/${selectedSession.id}`, payload);
      
      toast.success('Session mise à jour');
      setShowEditModal(false);
      fetchSessions();
    } catch (error) {
      console.error('Error updating live session:', error);
      toast.error(getApiErrorMessage(error, 'Erreur lors de la mise à jour'));
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm('Supprimer cette session ?')) return;
    
    try {
      await api.delete(`/live-sessions/${sessionId}`);
      toast.success('Session supprimée');
      fetchSessions();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleStart = async (sessionId) => {
    try {
      await api.post(`/live-sessions/${sessionId}/start`);
      toast.success('Session démarrée - Participants notifiés');
      fetchSessions();
    } catch (error) {
      toast.error('Erreur lors du démarrage');
    }
  };

  const handleEnd = async (sessionId) => {
    try {
      await api.post(`/live-sessions/${sessionId}/end`);
      toast.success('Session terminée');
      fetchSessions();
    } catch (error) {
      toast.error('Erreur lors de la terminaison');
    }
  };

  const handleAddReplay = async () => {
    if (!replayUrl) {
      toast.error('Veuillez entrer l\'URL du replay');
      return;
    }
    
    try {
      await api.post(`/live-sessions/${selectedSession.id}/add-replay?replay_url=${encodeURIComponent(replayUrl)}`);
      toast.success('Replay ajouté - Participants notifiés');
      setShowReplayModal(false);
      fetchSessions();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du replay');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-100 text-blue-700',
      live: 'bg-red-100 text-red-700 animate-pulse',
      completed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-slate-100 text-slate-700'
    };
    const labels = {
      scheduled: 'À venir',
      live: 'EN DIRECT',
      completed: 'Terminée',
      cancelled: 'Annulée'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.scheduled}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Stats
  const scheduledCount = sessions.filter(s => s.status === 'scheduled').length;
  const liveCount = sessions.filter(s => s.status === 'live').length;
  const completedCount = sessions.filter(s => s.status === 'completed').length;
  const withReplayCount = sessions.filter(s => s.replay_available).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Header onMenuClick={onMenuClick} />
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <Video className="w-8 h-8 text-violet-600" />
              Sessions Live
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              Gérez vos webinaires et sessions en direct
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={fetchSessions} data-testid="refresh-sessions-btn" title="Actualiser">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={openCreateModal} className="bg-violet-600 hover:bg-violet-700">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle session
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{scheduledCount}</p>
                  <p className="text-xs text-blue-600">À venir</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-red-50 dark:bg-red-900/20 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500 rounded-lg">
                  <Play className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-700">{liveCount}</p>
                  <p className="text-xs text-red-600">En direct</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500 rounded-lg">
                  <Square className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{completedCount}</p>
                  <p className="text-xs text-emerald-600">Terminées</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-violet-50 dark:bg-violet-900/20 border-violet-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-500 rounded-lg">
                  <MonitorPlay className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-violet-700">{withReplayCount}</p>
                  <p className="text-xs text-violet-600">Avec replay</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Filter className="w-5 h-5 text-slate-500" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded-md bg-white dark:bg-slate-800"
              >
                <option value="">Tous les statuts</option>
                <option value="scheduled">À venir</option>
                <option value="live">En direct</option>
                <option value="completed">Terminées</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Sessions List */}
        <Card>
          <CardHeader>
            <CardTitle>Sessions ({sessions.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {sessions.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Video className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucune session live</p>
                <Button onClick={openCreateModal} className="mt-4" variant="outline">
                  Créer une session
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {sessions.map(session => (
                  <div 
                    key={session.id}
                    className={`p-4 border rounded-lg hover:shadow-md transition-shadow ${
                      session.status === 'live' ? 'border-red-300 bg-red-50 dark:bg-red-900/10' : ''
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{session.title}</h3>
                          {getStatusBadge(session.status)}
                          {session.replay_available && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-700">
                              Replay dispo
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {session.scheduled_date ? new Date(session.scheduled_date).toLocaleDateString('fr-FR') : 'Non planifiée'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {session.start_time} - {session.end_time} ({session.duration_minutes}min)
                          </span>
                          {session.group_name && (
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {session.group_name}
                            </span>
                          )}
                          {session.trainer_name && (
                            <span className="flex items-center gap-1 text-violet-600">
                              {session.trainer_name}
                            </span>
                          )}
                          {session.meeting_platform_label && (
                            <span className="flex items-center gap-1">
                              <Link2 className="w-4 h-4" />
                              {session.meeting_platform_label}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {session.status === 'scheduled' && (
                          <Button 
                            size="sm" 
                            onClick={() => handleStart(session.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Démarrer
                          </Button>
                        )}
                        
                        {session.status === 'live' && (
                          <>
                            {session.live_url && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => window.open(session.live_url, '_blank')}
                              >
                                <ExternalLink className="w-4 h-4 mr-1" />
                                Rejoindre
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              onClick={() => handleEnd(session.id)}
                              className="bg-slate-600 hover:bg-slate-700"
                            >
                              <Square className="w-4 h-4 mr-1" />
                              Terminer
                            </Button>
                          </>
                        )}
                        
                        {session.status === 'completed' && !session.replay_available && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openReplayModal(session)}
                          >
                            <MonitorPlay className="w-4 h-4 mr-1" />
                            Ajouter replay
                          </Button>
                        )}
                        
                        {session.replay_url && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(session.replay_url, '_blank')}
                          >
                            <MonitorPlay className="w-4 h-4 mr-1" />
                            Voir replay
                          </Button>
                        )}
                        
                        <Button variant="ghost" size="sm" onClick={() => openDetailModal(session)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditModal(session)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(session.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-violet-600" />
              Nouvelle session live
            </DialogTitle>
            <DialogDescription>
              Créez une session et invitez les participants
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Titre *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Ex: Session de révision - Module 1"
                  data-testid="live-title-input"
                />
              </div>
              
              <div className="sm:col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Description de la session..."
                  rows={2}
                />
              </div>
              
              <div>
                <Label>Groupe</Label>
                <select
                  value={formData.group_id}
                  onChange={(e) => setFormData({...formData, group_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-800"
                  data-testid="live-group-select"
                >
                  <option value="">
                    {groups.length === 0 ? 'Aucun groupe disponible' : 'Sélectionner un groupe'}
                  </option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id}>
                      {g.name}{g.course_name ? ` (${g.course_name})` : ''}
                    </option>
                  ))}
                </select>
                {groups.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Aucun groupe trouvé. Créez-en un dans Classes / Groupes, puis rouvrez cette fenêtre.
                  </p>
                )}
              </div>
              
              <div>
                <Label>Formateur</Label>
                <select
                  value={formData.trainer_id}
                  onChange={(e) => setFormData({...formData, trainer_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-800"
                  data-testid="live-trainer-select"
                >
                  <option value="">Sélectionner un formateur</option>
                  {trainers.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.first_name || t.name || ''} {t.last_name || ''}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Cours (optionnel)</Label>
                <select
                  value={formData.course_id}
                  onChange={(e) => setFormData({...formData, course_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-800"
                >
                  <option value="">Sélectionner un cours</option>
                  {Array.isArray(courses) && courses.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Plateforme</Label>
                <select
                  value={formData.meeting_platform}
                  onChange={(e) => setFormData({...formData, meeting_platform: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-800"
                >
                  {PLATFORMS.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                  data-testid="live-date-input"
                />
              </div>
              
              <div>
                <Label>Heure de début *</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                  data-testid="live-time-input"
                />
              </div>
              
              <div>
                <Label>Durée (minutes)</Label>
                <Input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value) || 60})}
                />
              </div>
              
              <div className="sm:col-span-2">
                <Label>Lien de la réunion</Label>
                <Input
                  value={formData.live_url}
                  onChange={(e) => setFormData({...formData, live_url: e.target.value})}
                  placeholder="https://zoom.us/j/... ou https://meet.google.com/..."
                />
              </div>
              
              <div>
                <Label>ID de réunion (optionnel)</Label>
                <Input
                  value={formData.meeting_id}
                  onChange={(e) => setFormData({...formData, meeting_id: e.target.value})}
                  placeholder="123 456 789"
                />
              </div>
              
              <div>
                <Label>Mot de passe (optionnel)</Label>
                <Input
                  value={formData.meeting_password}
                  onChange={(e) => setFormData({...formData, meeting_password: e.target.value})}
                  placeholder="abc123"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate} className="bg-violet-600 hover:bg-violet-700">
              Créer la session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier la session</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Titre</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                />
              </div>
              
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={2}
                />
              </div>
              
              <div>
                <Label>Groupe</Label>
                <select
                  value={formData.group_id}
                  onChange={(e) => setFormData({...formData, group_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-800"
                >
                  <option value="">
                    {groups.length === 0 ? 'Aucun groupe disponible' : 'Sélectionner'}
                  </option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id}>
                      {g.name}{g.course_name ? ` (${g.course_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Formateur</Label>
                <select
                  value={formData.trainer_id}
                  onChange={(e) => setFormData({...formData, trainer_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-800"
                >
                  <option value="">Sélectionner</option>
                  {trainers.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.first_name} {t.last_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Heure</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Plateforme</Label>
                <select
                  value={formData.meeting_platform}
                  onChange={(e) => setFormData({...formData, meeting_platform: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-800"
                >
                  {PLATFORMS.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Durée (min)</Label>
                <Input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value) || 60})}
                />
              </div>
              
              <div className="col-span-2">
                <Label>Lien réunion</Label>
                <Input
                  value={formData.live_url}
                  onChange={(e) => setFormData({...formData, live_url: e.target.value})}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdate} className="bg-violet-600 hover:bg-violet-700">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-violet-600" />
              {selectedSession?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedSession && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3">
                {getStatusBadge(selectedSession.status)}
                {selectedSession.replay_available && (
                  <span className="px-2 py-1 rounded-full text-xs bg-violet-100 text-violet-700">
                    Replay disponible
                  </span>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Date</p>
                  <p className="font-medium">
                    {selectedSession.scheduled_date ? new Date(selectedSession.scheduled_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Horaire</p>
                  <p className="font-medium">{selectedSession.start_time} - {selectedSession.end_time}</p>
                </div>
                <div>
                  <p className="text-slate-500">Groupe</p>
                  <p className="font-medium">{selectedSession.group_name || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Formateur</p>
                  <p className="font-medium">{selectedSession.trainer_name || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Plateforme</p>
                  <p className="font-medium">{selectedSession.meeting_platform_label || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Durée</p>
                  <p className="font-medium">{selectedSession.duration_minutes} min</p>
                </div>
              </div>
              
              {selectedSession.description && (
                <div>
                  <p className="text-slate-500 text-sm">Description</p>
                  <p className="mt-1">{selectedSession.description}</p>
                </div>
              )}
              
              {selectedSession.live_url && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-700 mb-1">Lien de la réunion</p>
                  <a 
                    href={selectedSession.live_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
                  >
                    {selectedSession.live_url}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  {selectedSession.meeting_id && (
                    <p className="text-sm mt-1">ID: {selectedSession.meeting_id}</p>
                  )}
                  {selectedSession.meeting_password && (
                    <p className="text-sm">Mot de passe: {selectedSession.meeting_password}</p>
                  )}
                </div>
              )}
              
              {selectedSession.replay_url && (
                <div className="p-3 bg-violet-50 rounded-lg">
                  <p className="text-sm font-medium text-violet-700 mb-1">Replay</p>
                  <a 
                    href={selectedSession.replay_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-violet-600 hover:underline flex items-center gap-1 text-sm"
                  >
                    Voir le replay
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replay Modal */}
      <Dialog open={showReplayModal} onOpenChange={setShowReplayModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MonitorPlay className="w-5 h-5 text-violet-600" />
              Ajouter le replay
            </DialogTitle>
            <DialogDescription>
              Les participants seront notifiés de la disponibilité du replay
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>URL du replay</Label>
            <Input
              value={replayUrl}
              onChange={(e) => setReplayUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=... ou lien direct"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReplayModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddReplay} className="bg-violet-600 hover:bg-violet-700">
              Ajouter le replay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveSessionsAdminPage;
